/**
 * CSV serialisation for the table's current view (spec: 2026-09-10 §1).
 *
 * Pure and React-free on purpose: the value rules are where the bugs live, and they are
 * worth testing without a DOM. CrudPage supplies the columns already resolved to
 * `(row) => string`, so nothing here knows about display kinds or translation.
 */

/** One exported column: a header and a way to flatten a row to text. */
export interface CsvColumn<TEntity> {
  header: string;
  value: (row: TEntity) => string;
}

/**
 * Excel treats a leading =, +, - or @ as the start of a formula, so a crafted cell value
 * executes on open. Prefixing an apostrophe neutralises it while keeping the text readable,
 * which is better than dropping characters the reader expected to see.
 */
const FORMULA_LEAD = /^[=+\-@]/;

/** Fields containing these must be quoted (RFC 4180). */
const NEEDS_QUOTES = /[",\r\n]/;

export function escapeCsvValue(raw: string): string {
  const neutralised = FORMULA_LEAD.test(raw) ? `'${raw}` : raw;
  if (!NEEDS_QUOTES.test(neutralised) && neutralised === raw) {
    return neutralised;
  }
  // A quote inside a quoted field is written doubled.
  return `"${neutralised.replace(/"/g, '""')}"`;
}

/**
 * A BOM, because the data is Vietnamese and Excel decodes a BOM-less UTF-8 file as the
 * system codepage — every accented character arrives mangled.
 */
export const CSV_BOM = '﻿';

export function buildCsv<TEntity>(columns: CsvColumn<TEntity>[], rows: TEntity[]): string {
  const lines = [columns.map((column) => escapeCsvValue(column.header)).join(',')];
  for (const row of rows) {
    lines.push(columns.map((column) => escapeCsvValue(column.value(row))).join(','));
  }
  // CRLF: RFC 4180's line ending, and the one Excel on Windows expects.
  return CSV_BOM + lines.join('\r\n');
}

/** `menu-item-variants-2026-09-10.csv` */
export function csvFilename(resourceKey: string, now: Date = new Date()): string {
  const date = [
    now.getFullYear(),
    String(now.getMonth() + 1).padStart(2, '0'),
    String(now.getDate()).padStart(2, '0'),
  ].join('-');
  const slug = resourceKey
    .replace(/[^a-z0-9]+/gi, '-')
    .replace(/^-|-$/g, '')
    .toLowerCase();
  return `${slug || 'export'}-${date}.csv`;
}

/**
 * Hands the file to the browser. Guarded for non-DOM environments (the package's own tests
 * import this module) rather than assuming a document.
 */
export function downloadCsv(filename: string, csv: string): void {
  if (typeof document === 'undefined' || typeof URL.createObjectURL !== 'function') {
    return;
  }
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}
